import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { Alert, IconButton, InputAdornment, LinearProgress, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { FC, useEffect, useState } from 'react';

import useUser from '../../../../hooks/useUser';
import { ChatTurn } from '../../interfaces/models';
import { deleteConversation, getConversation, listConversations } from '../../services/models';

export const HISTORY_KEY = 'gargi-history';

// This person's earlier conversations with Gargi for the business. Opening one brings it back to continue.
const ChatHistory: FC<{ onOpen: (id: string, turns: ChatTurn[]) => void; currentId: string }> = ({ onOpen, currentId }) => {
  const { selectedBusiness } = useUser();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => { const t = setTimeout(() => setQuery(search.trim()), 300); return () => clearTimeout(t); }, [search]);
  const list = useQuery({
    queryKey: [HISTORY_KEY, selectedBusiness?.id, query], queryFn: () => listConversations(query), enabled: !!selectedBusiness?.id,
    placeholderData: (previous) => previous,
  });
  const open = useMutation({ mutationFn: getConversation, onSuccess: (c) => onOpen(c.id, c.turns) });
  const remove = useMutation({
    mutationFn: deleteConversation,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [HISTORY_KEY, selectedBusiness?.id] }),
  });

  const items = list.data ?? [];
  return (
    <Stack spacing={1}>
      <Typography variant='overline' color='text.secondary'>Your conversations</Typography>
      <TextField size='small' placeholder='Search your conversations' value={search} onChange={(e) => setSearch(e.target.value)}
        InputProps={{ startAdornment: <InputAdornment position='start'><SearchRoundedIcon fontSize='small' /></InputAdornment> }} />
      {list.isFetching && <LinearProgress sx={{ borderRadius: 1 }} />}
      {list.isError && <Alert severity='warning'>Your earlier conversations could not be loaded. Try again in a moment.</Alert>}
      {!list.isLoading && !items.length && (
        <Typography variant='body2' color='text.secondary'>
          {query ? `No conversation mentions “${query}”.` : 'Nothing yet. Conversations are saved here after each answer.'}
        </Typography>
      )}
      {open.isPending && <LinearProgress sx={{ borderRadius: 1 }} />}
      {open.isError && <Alert severity='warning'>That conversation is no longer available.</Alert>}
      {items.map((c) => (
        <Paper key={c.id} variant='outlined' onClick={() => !open.isPending && open.mutate(c.id)}
          sx={{ px: 1.75, py: 1.1, borderRadius: 2, cursor: 'pointer', borderColor: c.id === currentId ? 'primary.main' : undefined,
            '&:hover': { borderColor: 'primary.main' } }}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Stack sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant='body2' fontWeight={600} noWrap>{c.title}</Typography>
              <Typography variant='caption' color='text.secondary'>
                {moment.unix(c.updated).fromNow()} · {c.questions} question{c.questions === 1 ? '' : 's'}
              </Typography>
            </Stack>
            <Tooltip title='Delete'>
              <IconButton size='small' onClick={(e) => { e.stopPropagation(); remove.mutate(c.id); }} disabled={remove.isPending}>
                <DeleteOutlineRoundedIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};

export default ChatHistory;
